import { type ClientContext, ContextClient } from '~/services/contextClient';
import MessageStandardsClientFactory from './messageStandardsClient.factory';
import type {
    MessageStandardsClientInterface,
    MessageStandardType,
    UpdateTargetGroupsRequest,
    UpdateTargetGroupsResponse,
    GroupMappingType,
} from './messageStandardsClient.types';
import type { QueryParams, CursorPaginationResponse } from '~/services/api';
import type { AxiosPromise } from 'axios';

class MessageStandardsClient extends ContextClient<MessageStandardsClientInterface> {
    constructor() {
        super(MessageStandardsClientFactory);
    }

    getMessageStandards(
        context: ClientContext,
        params?: QueryParams
    ): AxiosPromise<CursorPaginationResponse<MessageStandardType>> {
        const client = this.getClient(context);
        return client.getMessageStandards(params);
    }

    getMessageStandard(context: ClientContext, messageStandardId: number): AxiosPromise<MessageStandardType> {
        const client = this.getClient(context);
        return client.getMessageStandard(messageStandardId);
    }

    createMessageStandard(context: ClientContext, data: MessageStandardType): AxiosPromise<MessageStandardType> {
        const client = this.getClient(context);
        return client.createMessageStandard(data);
    }

    updateMessageStandard(
        context: ClientContext,
        messageStandardId: number,
        data: MessageStandardType
    ): AxiosPromise<MessageStandardType> {
        const client = this.getClient(context);
        return client.updateMessageStandard(messageStandardId, data);
    }

    deleteMessageStandard(context: ClientContext, messageStandardId: number): AxiosPromise<void> {
        const client = this.getClient(context);
        return client.deleteMessageStandard(messageStandardId);
    }

    getGroupMappings(context: ClientContext): AxiosPromise<{ data: GroupMappingType[] }> {
        const client = this.getClient(context);
        return client.getGroupMappings();
    }

    updateTargetGroups(
        context: ClientContext,
        messageStandardId: number,
        data: UpdateTargetGroupsRequest
    ): AxiosPromise<UpdateTargetGroupsResponse> {
        const client = this.getClient(context);
        return client.updateTargetGroups(messageStandardId, data);
    }

    getDefaultMessageStandard(context: ClientContext): AxiosPromise<MessageStandardType> {
        const client = this.getClient(context);
        return client.getDefaultMessageStandard();
    }

    updateDefaultMessageStandard(context: ClientContext, data: MessageStandardType): AxiosPromise<MessageStandardType> {
        const client = this.getClient(context);
        return client.updateDefaultMessageStandard(data);
    }

    getMessageStandardsGroupCounts(
        context: ClientContext,
        params: { custom_message_standard_ids: number[] }
    ): AxiosPromise<{ counts: Record<number, number> }> {
        const client = this.getClient(context);
        if (client.getMessageStandardsGroupCounts) {
            return client.getMessageStandardsGroupCounts(params);
        }
        throw new Error(`Get brand standards group counts is not supported by ${client.constructor.name}`);
    }
}

export default new MessageStandardsClient();
